import type { ComponentProps } from 'react'
import React, { useEffect, useState } from 'react'
import { Label } from '~/design-system/primitives'
import { cn } from '~/lib/cn'
import type { PaymentSystem } from '~/type/Cart'
import { EFlagCreditCard } from '~/types/cart.interface'

type CvvInputProps = {
  onCvvChange?: (cvv: string) => void
  creditCard?: PaymentSystem
} & ComponentProps<'input'>

const CvvInput: React.FC<CvvInputProps> = ({ onCvvChange, creditCard, ...props }) => {
  const {
    validator = {
      cardCodeRegex: '',
      cardCodeMask:
        creditCard?.name.toLocaleLowerCase() === EFlagCreditCard.AMERICAN_EXPRESS.toLocaleLowerCase()
          ? '0000'
          : '000',
    },
  } = creditCard || {}
  const cardCodeRegex = validator.cardCodeRegex
  const [cvv, setCvv] = useState('')
  const [isValid, setIsValid] = useState(true)
  const [hasStartedTyping, setHasStartedTyping] = useState(false)

  const maxLength =
    creditCard?.name.toLocaleLowerCase() === EFlagCreditCard.AMERICAN_EXPRESS.toLocaleLowerCase() ? 4 : 3

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    const dynamicRegex = new RegExp(String(cardCodeRegex))

    if (!hasStartedTyping && value.length > 0) {
      setHasStartedTyping(true)
    }

    if (value === '' || new RegExp(`^[0-9]{0,${maxLength}}$`).test(value)) {
      setCvv(value)
      setIsValid(value === '' || dynamicRegex.test(value))
      onCvvChange?.(value)
    }
  }

  useEffect(() => {
    const dynamicRegex = new RegExp(String(cardCodeRegex))

    if (
      creditCard?.name.toLocaleLowerCase() === EFlagCreditCard.AMERICAN_EXPRESS.toLocaleLowerCase() &&
      cvv.length === 3
    ) {
      setIsValid(false)
    } else if (cvv.length > 0) {
      setIsValid(dynamicRegex.test(cvv))
    }
  }, [cvv, creditCard?.name, cardCodeRegex])

  useEffect(() => {
    if (
      creditCard?.name.toLocaleLowerCase() !== EFlagCreditCard.AMERICAN_EXPRESS.toLocaleLowerCase() &&
      cvv.length === 4
    ) {
      setCvv('')
      setIsValid(true)
    }
  }, [creditCard?.name])

  return (
    <>
      <Label className="" htmlFor="cvc">
        CVV *
      </Label>

      <input
        className={cn(
          'max-w-full lg:max-w-[116px] w-full max-h-9 text-gray-800 text-sm border rounded border-gray-60 px-2 py-1.5 outline-none m-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          !isValid && hasStartedTyping && 'border border-red-700',
        )}
        id="cvv"
        maxLength={maxLength}
        placeholder={String(validator?.cardCodeMask)}
        type="tel"
        value={cvv}
        onChange={handleChange}
        {...props}
      />

      <div>
        <p className={cn('text-sm', isValid || !hasStartedTyping ? 'text-gray-80' : 'text-red-700')}>
          CVV{!isValid && hasStartedTyping ? ' inválido' : ''}
        </p>
      </div>
    </>
  )
}

export default CvvInput
